import React, { useState } from 'react';

import { useRouter } from 'next/navigation';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckIcon from '@mui/icons-material/Check';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HelpIcon from '@mui/icons-material/Help';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Box } from '@mui/material';

import { useUserInfo } from '@/context/UserInfoContext';
import { useFeedback } from '@/feedback';
import { useTheme } from '@/theme/ThemeContext';

import { SiteLogoWide } from '../SiteLogoWide';

interface OmniBoxMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

export const OmniBoxMenu: React.FC<OmniBoxMenuProps> = ({ anchorEl, open, onClose }) => {
  const [themeAnchorEl, setThemeAnchorEl] = useState<null | HTMLElement>(null);
  const themeMenuOpen = Boolean(themeAnchorEl);

  const { userInfo } = useUserInfo();
  const { openFeedback } = useFeedback();
  const { colorScheme, setColorScheme } = useTheme();
  const router = useRouter();

  if (!userInfo) {
    return null;
  }

  const handleMenuItemClick = (path: string) => {
    router.push(path);
    onClose();
  };

  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeAnchorEl(null);
  };

  const handleThemeSelect = (mode: 'light' | 'dark' | 'system') => {
    setColorScheme(mode);
    handleThemeMenuClose();
    onClose();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        disableEnforceFocus
        disableScrollLock
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              maxWidth: 280,
            },
          },
        }}
      >
        {/* Site logo at the top */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2,
            px: 2,
          }}
        >
          <SiteLogoWide heightPx={32} href="/" onClick={onClose} />
        </Box>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleMenuItemClick('/collections')}>
          <ListItemIcon>
            <BookmarkIcon />
          </ListItemIcon>
          <ListItemText>Starred items</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/account')}>
          <ListItemIcon>
            <AccountCircleIcon />
          </ListItemIcon>
          <ListItemText>Account</ListItemText>
        </MenuItem>
        {userInfo.may_invite && (
          <MenuItem onClick={() => handleMenuItemClick('/invite')}>
            <ListItemIcon>
              <GroupAddIcon />
            </ListItemIcon>
            <ListItemText>Invite</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuItemClick('/admin/schema')}>
          <ListItemIcon>
            <BugReportIcon />
          </ListItemIcon>
          <ListItemText>Debug</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/about/help')}>
          <ListItemIcon>
            <HelpIcon />
          </ListItemIcon>
          <ListItemText>Help</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            openFeedback({ target: null });
            onClose();
          }}
        >
          <ListItemIcon>
            <FeedbackOutlinedIcon />
          </ListItemIcon>
          <ListItemText>Give feedback</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleThemeMenuOpen}>
          <ListItemIcon>
            {colorScheme === 'light' ? (
              <LightModeIcon />
            ) : colorScheme === 'dark' ? (
              <DarkModeIcon />
            ) : (
              <SettingsBrightnessIcon />
            )}
          </ListItemIcon>
          <ListItemText>Theme</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleMenuItemClick('/logout')}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Theme submenu */}
      <Menu
        anchorEl={themeAnchorEl}
        open={themeMenuOpen}
        onClose={handleThemeMenuClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              maxWidth: 250,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleThemeSelect('system')}>
          <ListItemIcon>
            <SettingsBrightnessIcon />
            {colorScheme === 'system' && <CheckIcon sx={{ ml: 'auto', fontSize: 18 }} />}
          </ListItemIcon>
          <ListItemText>System</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleThemeSelect('light')}>
          <ListItemIcon>
            <LightModeIcon />
            {colorScheme === 'light' && <CheckIcon sx={{ ml: 'auto', fontSize: 18 }} />}
          </ListItemIcon>
          <ListItemText>Light</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleThemeSelect('dark')}>
          <ListItemIcon>
            <DarkModeIcon />
            {colorScheme === 'dark' && <CheckIcon sx={{ ml: 'auto', fontSize: 18 }} />}
          </ListItemIcon>
          <ListItemText>Dark</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
